import { useCallback } from 'react';

import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { CalendarWrapper as Calendar } from '@/components/common/calendar-wrapper';
import { useSites } from '@/services/sites';
import { formatDateWithPattern } from '@/utils';

type CsrDay7SelectorProps = {
  className?: string;
  totals: {
    frozen: number;
    implanted: number;
    discarded: number;
    shipped: number;
    d65shipped: number;
    d65Frozen: number;
  };
};

export const CsrDay7Selector = ({
  className,
  totals,
}: CsrDay7SelectorProps) => {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const beginDate = params.get('beginDate');
  const endDate = params.get('endDate');
  const siteId = params.get('site');
  const accountId = params.get('account');

  const { data: siteData } = useSites({ filter: 'fertilization' });

  const sites = siteData?.map((item) => ({ label: item.name, value: item.id }));

  const handleBeginDateChange = useCallback(
    (e: DropdownChangeEvent) => {
      const newParams = new URLSearchParams(params);
      if (e.value) {
        newParams.set(
          'beginDate',
          formatDateWithPattern(e.value, 'MM-dd-yyyy'),
        );
      } else {
        newParams.delete('beginDate');
      }
      setParams(newParams);
    },
    [params, setParams],
  );

  const handleEndDateChange = useCallback(
    (e: DropdownChangeEvent) => {
      const newParams = new URLSearchParams(params);
      if (e.value) {
        newParams.set('endDate', formatDateWithPattern(e.value, 'MM-dd-yyyy'));
      } else {
        newParams.delete('endDate');
      }
      setParams(newParams);
    },
    [params, setParams],
  );

  const handleSiteChange = useCallback(
    (e: DropdownChangeEvent) => {
      const newParams = new URLSearchParams(params);
      if (e.value) {
        newParams.set('site', e.value);
      } else {
        newParams.delete('site');
      }
      setParams(newParams);
    },
    [params, setParams],
  );

  return (
    <div className={`csr-day7__selector ${className || ''}`.trim()}>
      <Calendar
        value={beginDate ? new Date(beginDate) : null}
        dateFormat="mm-dd-yy"
        onChange={handleBeginDateChange}
        showIcon
        placeholder={t('fertilization.fertDate')}
      />
      <Calendar
        value={endDate ? new Date(endDate) : null}
        dateFormat="mm-dd-yy"
        onChange={handleEndDateChange}
        showIcon
        placeholder={t('transfer.transferDate')}
      />
      <Dropdown
        value={siteId}
        onChange={handleSiteChange}
        options={sites}
        placeholder={t('site.site')}
        className="site-dropdown"
        filter
        disabled={!beginDate || !endDate}
      />

      <div>
        <div className="csr-day7__selector__totals">
          <span>
            <strong>{t('embryo.frozen')}:</strong> {totals.frozen}
          </span>
          <span>
            <strong>{t('embryo.implanted')}:</strong> {totals.implanted}
          </span>
          <span>
            <strong>{t('embryo.discarded')}:</strong> {totals.discarded}
          </span>
        </div>
        <div className="csr-day7__selector__totals">
          <span>
            <strong>{t('shipping.shipped')}:</strong> {totals.shipped}
          </span>
          <span>
            <strong>{t('shipping.d65Shipped')}:</strong> {totals.d65shipped}
          </span>
          <span>
            <strong>{t('freeze.d65Frozen')}:</strong> {totals.d65Frozen}
          </span>
        </div>
      </div>
    </div>
  );
};
